import * as THREE from 'three';
import { PullDirection } from '../../../models/pull-direction.model';

export interface PullDirectionPickingState {
  raycaster: THREE.Raycaster;
  mouseNdc: THREE.Vector2;
  hoverHelper: THREE.ArrowHelper | null;
}

export function createPullDirectionPickingState(): PullDirectionPickingState {
  return {
    raycaster: new THREE.Raycaster(),
    mouseNdc: new THREE.Vector2(),
    hoverHelper: null,
  };
}

export function pickPullDirectionFromClick(
  event: MouseEvent,
  canvas: HTMLCanvasElement,
  camera: THREE.PerspectiveCamera,
  objectGroup: THREE.Group,
  state: PullDirectionPickingState,
): PullDirection | null {
  const hit = getPickIntersection(event, canvas, camera, objectGroup, state);
  if (!hit?.face?.normal) return null;

  const normalMatrix = new THREE.Matrix3().getNormalMatrix(
    hit.object.matrixWorld,
  );
  const worldNormal = hit.face.normal
    .clone()
    .applyMatrix3(normalMatrix)
    .normalize();

  return {
    x: worldNormal.x,
    y: worldNormal.y,
    z: worldNormal.z,
  };
}

export function updatePullDirectionHoverPreview(
  event: MouseEvent,
  canvas: HTMLCanvasElement,
  camera: THREE.PerspectiveCamera,
  objectGroup: THREE.Group,
  scene: THREE.Scene,
  state: PullDirectionPickingState,
): void {
  const hit = getPickIntersection(event, canvas, camera, objectGroup, state);
  if (!hit?.face?.normal || !hit.point) {
    clearPullDirectionHoverPreview(scene, state);
    return;
  }

  const normalMatrix = new THREE.Matrix3().getNormalMatrix(
    hit.object.matrixWorld,
  );
  const worldNormal = hit.face.normal
    .clone()
    .applyMatrix3(normalMatrix)
    .normalize();

  const box = new THREE.Box3().setFromObject(objectGroup);
  const size = box.getSize(new THREE.Vector3());
  const arrowLength = Math.max(Math.max(size.x, size.y), size.z) * 0.12;
  const length = arrowLength > 0 ? arrowLength : 10;

  clearPullDirectionHoverPreview(scene, state);
  state.hoverHelper = new THREE.ArrowHelper(
    worldNormal,
    hit.point,
    length,
    0x00cc00,
    length * 0.2,
    length * 0.08,
  );
  scene.add(state.hoverHelper);
}

export function clearPullDirectionHoverPreview(
  scene: THREE.Scene,
  state: PullDirectionPickingState,
): void {
  if (!state.hoverHelper) return;

  scene.remove(state.hoverHelper);
  state.hoverHelper.dispose();
  state.hoverHelper = null;
}

function getPickIntersection(
  event: MouseEvent,
  canvas: HTMLCanvasElement,
  camera: THREE.PerspectiveCamera,
  objectGroup: THREE.Group,
  state: PullDirectionPickingState,
): THREE.Intersection | null {
  const rect = canvas.getBoundingClientRect();
  state.mouseNdc.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  state.mouseNdc.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

  state.raycaster.setFromCamera(state.mouseNdc, camera);
  const intersections = state.raycaster.intersectObjects(
    objectGroup.children,
    true,
  );

  return (
    intersections.find(
      (intersect) =>
        intersect.object instanceof THREE.Mesh &&
        intersect.object.userData['overlayType'] === undefined,
    ) ?? null
  );
}
